import api from 'api';
import { EMPTY_ARRAY } from 'constants/defaults';
import { uniq } from 'lodash-es';
import { getIsViewingDemoUser } from 'redux/modules/users-page/demo/usersPage.demo.selectors';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import {
  createXhrStatusModule,
  xhrStatusFactory,
  xhrStatusQueryInfo,
} from 'redux/reusable/xhrStatus';
import type { IState } from 'redux/state';
import type { Thunk } from 'types/redux';
import type { RawCapacity, RawCapacityPayload } from 'types/summaries';
import { actionCreatorFactory } from 'typescript-fsa';
import { reducerWithInitialState } from 'typescript-fsa-reducers';

const actionCreator = actionCreatorFactory('timely/capacities');

export const setCapacities = actionCreator<Array<RawCapacityPayload>>('SET_CAPACITIES');
export const setUserCapacity = actionCreator<{ capacities: Array<RawCapacity>; userId: number }>(
  'SET_USER_CAPACITY'
);

export const { withXhrStatusReducer, wrapFetching } = createXhrStatusModule('capacities');

const readUserCapacityEntities = (state: IState) => state.capacities.entities;
const readXhrStatus = (state: IState) => state.capacities.xhrStatus;

export const getUserCapacitiesEntities = readUserCapacityEntities;

export const getXhrStatus = readXhrStatus;

export function fetchUserCapacity(userId: number): Thunk {
  return (dispatch, getState) => {
    // A demo person's capacity comes from their fixture; the id is fictional,
    // so there is nothing to fetch for it.
    if (getIsViewingDemoUser(getState())) {
      return Promise.resolve();
    }
    return dispatch(wrapFetching(api.getUserCapacity(userId))).then(
      (capacities: Array<RawCapacity>) => {
        dispatch(setUserCapacity({ capacities, userId }));
      }
    );
  };
}

export function fetchUserCapacities(): Thunk {
  return dispatch => {
    return dispatch(wrapFetching(api.getUserCapacities())).then(
      (capacities: Array<RawCapacityPayload>) => {
        dispatch(setCapacities(capacities));
      }
    );
  };
}

export function fetchUserCapacitiesSmartly(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const xhrStatus = xhrStatusQueryInfo(getXhrStatus(state), { key: 'default' });

    if (!xhrStatus.shouldFetch) {
      return Promise.resolve();
    }

    dispatch(fetchUserCapacities());
  };
}

export interface State {
  entities: Record<number, Array<RawCapacity>>;
  result: Array<number>;
  xhrStatus: XhrStatus;
}

const initialState: State = {
  entities: {},
  result: [],
  xhrStatus: xhrStatusFactory(),
};

const reducer = reducerWithInitialState(initialState)
  .case(setCapacities, (state, capacities): State => {
    const result = uniq(capacities.map(capacity => capacity.user_id));
    const entities = result.reduce<Record<number, Array<RawCapacity>>>((acc, userId) => {
      acc[userId] =
        capacities.find(capacity => capacity.user_id === userId)?.capacities ?? EMPTY_ARRAY;
      return acc;
    }, {});

    return {
      ...state,
      entities,
      result,
    };
  })
  .case(setUserCapacity, (state, { capacities, userId }): State => {
    return {
      ...state,
      entities: {
        ...state.entities,
        [userId]: capacities,
      },
      result: uniq([...state.result, userId]),
    };
  });

export default withXhrStatusReducer(reducer);
